import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import { Drug } from "common/models/types";

interface DrugBasicInfoProps {
  drug: Drug;
  onEdit?: () => void;
}

// 섹션 컨테이너 스타일
const SectionContainer = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 섹션 헤더 스타일
const SectionHeader = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// 제목과 이미지를 담는 컨테이너
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// 정보 컨테이너 스타일 - 2열 레이아웃
const InfoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

// 정보 열 스타일
const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// 의약품 기본 정보 항목 스타일
const InfoItem = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

// 정보 라벨 스타일
const InfoLabel = styled.div`
  flex: 0 0 120px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

// 정보 값 스타일
const InfoValue = styled.div`
  flex: 1;
  word-break: break-all;
`;

// 코드 값 스타일
const CodeValue = styled.div`
  font-family: "Courier New", monospace;
  word-break: break-all;
  flex: 1;
`;

const DrugBasicInfo: React.FC<DrugBasicInfoProps> = ({ drug, onEdit }) => {
  // 날짜 형식 변환 함수
  const formatDate = (dateString: Date | string | null | undefined): string => {
    if (!dateString) return "정보 없음";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "정보 없음";
    return date.toLocaleDateString();
  };

  // 의약품 이미지 URL 생성
  const drugImageUrl = drug.itemCode
    ? `https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/drugs/pictures/${drug.itemCode}.jpg`
    : null;

  return (
    <SectionContainer>
      <SectionHeader>
        <TitleContainer>
          {drugImageUrl && (
            <Image
              src={drugImageUrl}
              alt={drug.productName}
              height={32}
              width={32}
              objectFit="contain"
              showOnError={false}
              enableHoverZoom={true}
              zoomScale={7}
            />
          )}
          <Text textType="H2">{drug.productName}</Text>
        </TitleContainer>
        {onEdit && (
          <Button type="Icon" size="Medium" variant="Ghost" color="Tertiary" onClick={onEdit}>
            <Icon name="Pencil" size={20} />
          </Button>
        )}
      </SectionHeader>

      <InfoContainer>
        {/* 왼쪽 열 - 기본 정보 */}
        <InfoColumn>
          <InfoItem>
            <InfoLabel>제조사</InfoLabel>
            <InfoValue>{drug.companyName || "정보 없음"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>성분명</InfoLabel>
            <InfoValue>{drug.mainIngredient || "정보 없음"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>허가일자</InfoLabel>
            <InfoValue>{formatDate(drug.approvalDate)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>분류</InfoLabel>
            <InfoValue>{drug.specialtyOtc === "HFF" ? "건강기능식품" : drug.specialtyOtc || "정보 없음"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>투약량 단위</InfoLabel>
            <InfoValue>{drug.dosageUnit || "정보 없음"}</InfoValue>
          </InfoItem>
        </InfoColumn>

        {/* 오른쪽 열 - 코드 정보 */}
        <InfoColumn>
          <InfoItem>
            <InfoLabel>품목기준코드</InfoLabel>
            <CodeValue>{drug.itemCode || "정보 없음"}</CodeValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>표준코드</InfoLabel>
            <CodeValue>{drug.standardCode || "정보 없음"}</CodeValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>대표코드</InfoLabel>
            <CodeValue>{drug.mainCode || "정보 없음"}</CodeValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>제품코드</InfoLabel>
            <CodeValue>{drug.insuranceCode || "정보 없음"}</CodeValue>
          </InfoItem>
        </InfoColumn>
      </InfoContainer>
    </SectionContainer>
  );
};

export default DrugBasicInfo;
